import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "../data/site";
import { useSectionNav } from "../lib/nav";

const LINKS = [
  { label: "Ana Sayfa", hash: "" },
  { label: "Hakkımızda", hash: "hakkimizda" },
  { label: "Makinelerimiz", hash: "makineler" },
  { label: "Galeri", hash: "galeri" },
  { label: "İletişim", hash: "iletisim" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const nav = useSectionNav(navigate, location);

  const go = (hash) => (event) => {
    setOpen(false);
    nav(hash)(event);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line/80 bg-paper/85 backdrop-blur-md">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to="/" onClick={go("")} data-testid="header-logo" aria-label="Baytaş Tarım ana sayfa">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Ana menü">
          {LINKS.map((link) => (
            <a
              key={link.hash}
              href={link.hash ? `/#${link.hash}` : "/"}
              onClick={go(link.hash)}
              data-testid={`nav-${link.hash || "anasayfa"}`}
              className="text-sm font-semibold text-ink/70 transition-colors duration-200 hover:text-ink"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={SITE.phones[0].href}
            data-testid="header-phone"
            className="hidden items-center gap-2 text-sm font-semibold text-ink/70 transition-colors duration-200 hover:text-ink xl:inline-flex"
          >
            <Phone size={16} className="text-gold" />
            {SITE.phones[0].display}
          </a>
          <a
            href="#iletisim"
            onClick={go("iletisim")}
            data-testid="header-contact-cta"
            className="btn btn-primary hidden px-5 py-2.5 sm:inline-flex"
          >
            Bize Ulaşın
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-button"
            aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={open}
            className="grid h-10 w-10 place-items-center rounded-md border border-line bg-white text-ink lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-x-0 top-full border-b border-line bg-paper/95 shadow-lg backdrop-blur-md lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-8" aria-label="Mobil menü">
              {LINKS.map((link) => (
                <a
                  key={link.hash}
                  href={link.hash ? `/#${link.hash}` : "/"}
                  onClick={go(link.hash)}
                  data-testid={`mobile-nav-${link.hash || "anasayfa"}`}
                  className="border-b border-line/70 py-3.5 font-display text-lg font-bold uppercase tracking-tight text-ink last:border-0"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#iletisim"
                onClick={go("iletisim")}
                data-testid="mobile-contact-cta"
                className="btn btn-primary mt-4 w-full"
              >
                Bize Ulaşın
              </a>
              <div className="mt-4 flex flex-col items-center gap-2">
                {SITE.phones.map((p) => (
                  <a
                    key={p.href}
                    href={p.href}
                    data-testid={`mobile-phone-${p.display.replace(/\s/g, "")}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-ink/70"
                  >
                    <Phone size={16} className="text-gold" />
                    {p.display}
                  </a>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}